import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CategoryDropdown from './CategoryDropdown';
import { categories } from '@/mocks/categories';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const textColorClass = scrolled
    ? 'text-foreground-900'
    : 'text-white';
  const logoAccentClass = scrolled
    ? 'text-primary-500'
    : 'text-primary-300';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-10 py-4 max-w-[1440px] mx-auto">
        <Link to="/" className={`flex items-center gap-2 cursor-pointer ${textColorClass}`}>
          <span className="font-heading text-xl md:text-2xl font-extrabold whitespace-nowrap">
            OOPBuy <span className={logoAccentClass}>Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            className={`text-sm font-semibold hover:opacity-80 transition-opacity duration-200 whitespace-nowrap cursor-pointer ${textColorClass}`}
          >
            Home
          </Link>
          <Link
            to="/"
            className={`text-sm font-semibold hover:opacity-80 transition-opacity duration-200 whitespace-nowrap cursor-pointer ${textColorClass}`}
          >
            Shopping Guide
          </Link>
          <CategoryDropdown scrolled={scrolled} />
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'bg-foreground-900 text-white hover:bg-foreground-800'
                : 'bg-white text-foreground-900 hover:bg-white/90'
            }`}
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
            scrolled ? 'text-foreground-900 hover:bg-background-100' : 'text-white hover:bg-white/10'
          }`}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <i className="ri-close-line text-2xl"></i>
          ) : (
            <i className="ri-menu-line text-2xl"></i>
          )}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-background-50 text-foreground-900 font-semibold text-base hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-900 text-white">
                <i className="ri-home-4-line text-base"></i>
              </span>
              Home
            </Link>
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-background-50 text-foreground-900 font-semibold text-base hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Shopping Guide
            </Link>
            <div className="border-t border-background-200 my-3 pt-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-foreground-500 px-4 mb-2">
                Categories
              </h4>
              <MobileCategoryList onSelect={() => setMobileMenuOpen(false)} />
            </div>
            <div className="border-t border-background-200 my-3 pt-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-4 rounded-full bg-foreground-900 text-white font-semibold text-base hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-shopping-bag-line text-lg"></i>
                Buy It Now on Kaspreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileCategoryList({ onSelect }: { onSelect: () => void }) {
  return (
    <div className="flex flex-col gap-1">
      {categories.map((cat) => (
        <a
          key={cat.slug}
          href={cat.url}
          target="_blank"
          rel="noopener nofollow"
          onClick={onSelect}
          className="flex items-center justify-between px-4 py-3 rounded-lg text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
        >
          <span>{cat.name}</span>
          <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
        </a>
      ))}
    </div>
  );
}